/**
 * Triggers the PA order update rules engine to re-evaluate all active rules
 * (calling, POC assignment, POC reassignment) against current orders.
 */
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import {
  createRequestOptions,
  getAuthHeaders,
  handleApiResponse,
} from "../utils/apiHelpers";

export interface RunPaOrderUpdateWithRulesResponse {
  success: boolean;
  message: string;
  [key: string]: any;
}

/**
 * Invokes the PA order-update-with-rules run endpoint.
 *
 * @param rulesPaths - Firestore paths of the rule documents to evaluate
 * @param signal - Optional AbortSignal for canceling the request
 * @returns Promise with the API response
 */
export const runPaOrderUpdateWithRules = async (
  rulesPaths: string[],
  signal?: AbortSignal,
): Promise<RunPaOrderUpdateWithRulesResponse> => {
  const payload: Record<string, unknown> = {
    invocation: "api",
    rules_paths: rulesPaths,
  };

  const headers = await getAuthHeaders();
  const requestOptions = createRequestOptions(payload, headers, signal);

  try {
    const response = await fetch(
      API_ENDPOINTS.RUN_PA_ORDER_UPDATE_WITH_RULES,
      requestOptions,
    );
    const result = await handleApiResponse(response);
    logDataToConsole("runPaOrderUpdateWithRules", result);
    return result;
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      logDataToConsole("runPaOrderUpdateWithRules", "Request aborted");
      throw error;
    }
    logError(error as Error, "Error in runPaOrderUpdateWithRules");
    throw error;
  }
};
